import parse, { domToReact } from "html-react-parser";
import React from "react";

import { useTranslation } from "react-i18next";
import Markdown from "react-markdown";
import { map, meanBy, round, flatMap } from "lodash";
import { Assessment } from "@/features/assessments/graphql/generated";
import { Person } from "@/features/people/graphql/generated";
import ProfileHeader from "@/features/profile/components/ProfileHeader";
import {
  PDFDocument,
  PdfContainer,
  PageStartWrapper,
  NoSplitWrapper,
} from "../generator";
import { Column, Row } from "@/shared/ui/Blocks";
import { IconButton } from "@/shared/ui/IconButton";
import { SectionHeader } from "@/shared/ui/SectionHeader";
import { SubtitleText } from "@/shared/ui/Typography";
import { Title } from "@/shared/ui/Title";
import { EvaluationScoreRow } from "@/shared/ui/EvaluationScoreRow";
import { EvaluationRow } from "@/shared/widgets/PersonEvaluationsHoverOver";
import { AssessmentReportRowGroup } from "@/features/assessments/components/AssessmentReportRowGroup";
import { EvaluationsSummaryTable } from "@/shared/ui/EvaluationsSummaryTable";
import { SummarySection } from "@/features/assessments/pages/AssessmentReportPage";
import { cleanupMarkdownFromTags } from "@/shared/utils/parsers/cleanupMarkdownFromTags";

interface AssessmentReportPdfProps {
  profile: Person;
  assessment: Assessment;
  assessmentRowGroups: any; // TODO: Replace with actual type
  logoUrl?: string;
  summarySections: SummarySection[];
}

function AssessmentReportPdf(props: AssessmentReportPdfProps) {
  const {
    profile = {} as Person,
    assessment,
    assessmentRowGroups,
    logoUrl,
    summarySections,
  } = props;

  const options = {
    replace: (domNode: any) => {
      if (domNode.name === "ul") {
        return (
          <ul className="list-disc pl-4">
            {domToReact(domNode.children, options)}
          </ul>
        );
      }
      if (domNode.name === "li") {
        return (
          <li className="text-grey-700 mb-4">
            {domToReact(domNode.children, options)}
          </li>
        );
      }
    },
  };

  if (!profile || !assessment) return null;

  const { t } = useTranslation();

  const Evaluations = map(assessmentRowGroups, (group, groupName) => (
    <NoSplitWrapper key={groupName} className="space-y-8 mb-16">
      <Title as="h4" icon="Flag" description={group.description}>
        {groupName || ""}
      </Title>
      {map(group, (r, idx) => {
        const averageScore = meanBy(r.evaluations, "evaluation") || 0;
        return (
          <NoSplitWrapper key={idx} className="mb-8">
            <SectionHeader
              as="h5"
              title={r.title}
              className="flex-row"
              RightComponent={
                <Row className="items-center space-x-2">
                  <EvaluationScoreRow score={averageScore} />
                  <SubtitleText className="whitespace-nowrap">
                    {`${averageScore} out of 4`}
                  </SubtitleText>
                </Row>
              }
            />
            <Column className="space-y-4 mb-4">
              {map(r.evaluations, (evaluation, evalIdx) => (
                <EvaluationRow key={evalIdx} evaluation={evaluation} />
              ))}
            </Column>
            <Markdown>{r.summary || ""}</Markdown>
          </NoSplitWrapper>
        );
      })}
    </NoSplitWrapper>
  ));

  const pages = [
    <PdfContainer className="relative" key="assessment-report-pdf">
      {/* First page with profile information - add top margin to avoid overlap with header */}{" "}
      {/* Increased top padding to 6rem (24px) */}
      <NoSplitWrapper>
        <ProfileHeader
          initials={profile.initials || ""}
          name={profile.name || ""}
          avatarUrl={profile.avatar || ""}
          className="pl-8"
        />
        <Row className="border-b border-solid border-grey-100 py-4 justify-between">
          <IconButton
            color="black"
            variant="ghost"
            icon="Phone"
            label={profile.phone?.toString()}
          />
          <IconButton
            color="black"
            variant="ghost"
            icon="Email"
            label={profile.email?.toString()}
          />
          {profile.linkedin ? (
            <IconButton
              variant="ghost"
              icon="LinkedIn"
              label={profile.linkedin?.toString()}
            />
          ) : (
            <div />
          )}
        </Row>
        <EvaluationsSummaryTable
          rows={map(assessmentRowGroups, (group, title) => ({
            id: title,
            title,
            evaluation: round(
              meanBy(
                flatMap(group, (r) => r.evaluations),
                "evaluation"
              )
            ),
          }))}
        />
        {map(summarySections, (section) => (
          <Column className="mt-8 gap-4" key={section.title}>
            <Title as="h3">{section.title}</Title>
            <Markdown>{cleanupMarkdownFromTags(section.value || "")}</Markdown>
          </Column>
        ))}
      </NoSplitWrapper>
      {map(assessmentRowGroups, (group, title) => (
        <NoSplitWrapper key={title}>
          <AssessmentReportRowGroup key={title} title={title} rows={group} />
        </NoSplitWrapper>
      ))}
    </PdfContainer>,
  ];

  return <PDFDocument pages={pages} />;
}

export default AssessmentReportPdf;
